import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Alert,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  CardActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import {
  FitnessCenter as GymIcon,
  Pool as PoolIcon,
  SportsBasketball as CourtIcon,
  SelfImprovement as StudioIcon,
  DirectionsRun as TrackIcon,
  Grass as FieldIcon,
  MeetingRoom as RoomIcon,
  Schedule as ScheduleIcon,
  People as CapacityIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { isTrainerExtendedFeaturesEnabled } from '../utils/featureFlags';

const ViewFacilitiesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userPlan, setUserPlan] = useState(null);

  // Redirect trainers if extended features are disabled
  useEffect(() => {
    if (user?.role === 'trainer' && !isTrainerExtendedFeaturesEnabled()) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      
      // Use plan-based endpoint if user is authenticated and has a plan
      const endpoint = user && user.membershipPlan ? '/facilities/my-plan' : '/facilities';
      const response = await api.get(endpoint);
      
      setFacilities(response.data.data);
      if (response.data.userPlan) {
        setUserPlan(response.data.userPlan);
      }
      setError('');
    } catch (err) {
      // Fallback to public facilities if plan-based fails
      if (user && err.response?.status === 400) {
        try {
          const fallbackResponse = await api.get('/facilities');
          setFacilities(fallbackResponse.data.data);
          setError(t('facilities:errors.planNotFound'));
        } catch (fallbackErr) {
          setError(t('facilities:errors.fetchFailed'));
        }
      } else {
        setError(t('facilities:errors.fetchFailed'));
      }
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFacilityIcon = (type) => {
    const iconProps = { fontSize: 'large', color: 'primary' };
    switch (type) {
      case 'gym':
        return <GymIcon {...iconProps} />;
      case 'pool':
        return <PoolIcon {...iconProps} />;
      case 'court':
        return <CourtIcon {...iconProps} />;
      case 'studio':
        return <StudioIcon {...iconProps} />;
      case 'track':
        return <TrackIcon {...iconProps} />;
      case 'field':
        return <FieldIcon {...iconProps} />;
      case 'room':
        return <RoomIcon {...iconProps} />;
      default:
        return <GymIcon {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatOperatingHours = (operatingHours) => {
    if (!operatingHours || operatingHours.length === 0) {
      return [{ day: t('facilities:operatingHours.noHours'), time: '' }];
    }

    return operatingHours.map(hours => ({
      day: hours.day,
      time: hours.isClosed ? t('facilities:operatingHours.closed') : `${hours.openTime} - ${hours.closeTime}`
    }));
  };

  const formatBookingConfig = (bookingConfig) => {
    if (!bookingConfig) {
      return {
        slotDuration: t('facilities:booking.defaultSlot'),
        leadTime: t('facilities:booking.defaultLead'),
        maxDuration: t('facilities:booking.defaultMax')
      };
    }

    return {
      slotDuration: t('facilities:booking.slotDuration', { minutes: bookingConfig.slotDuration }),
      leadTime: t('facilities:booking.leadTime', { count: bookingConfig.leadTime }),
      maxDuration: t('facilities:booking.maxDuration', { count: Math.floor(bookingConfig.maxBookingDuration / 60) })
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {userPlan ? t('facilities:title.available') : t('facilities:title.our')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {userPlan 
            ? t('facilities:subtitle.planIncluded', { plan: userPlan.name })
            : t('facilities:subtitle.explore')
          }
        </Typography>
        {userPlan && (
          <Chip 
            label={`${userPlan.name} - ${userPlan.memberType}`}
            color="primary"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {facilities.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {userPlan 
              ? t('facilities:empty.noPlanFacilities')
              : t('facilities:empty.noFacilities')
            }
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={4} sx={{ width: '100%' }}>
          {facilities.map((facility) => {
            const operatingHours = formatOperatingHours(facility.operatingHours);
            const bookingConfig = formatBookingConfig(facility.bookingConfig);

            return (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={facility._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: (theme) => theme.shadows[8]
                    }
                  }}
                >
                  {/* Facility Icon Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 3,
                      bgcolor: 'grey.50'
                    }}
                  >
                    {getFacilityIcon(facility.type)}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Facility Name and Type */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h5" component="h2" gutterBottom>
                        {facility.name}
                      </Typography>
                      <Chip
                        label={facility.type}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>

                    {/* Status and Access Info */}
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={facility.status}
                        color={getStatusColor(facility.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {facility.accessInfo && (
                        <>
                          <Chip
                            label={facility.accessInfo.accessType.replace('-', ' ').replace('_', ' ')}
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {facility.accessInfo.requiresPayment && (
                            <Chip
                              label={t('facilities:access.payPerUse')}
                              color="warning"
                              variant="outlined"
                              size="small"
                            />
                          )}
                          {facility.accessInfo.requiresApproval && (
                            <Chip
                              label={t('facilities:access.approvalRequired')}
                              color="secondary"
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </>
                      )}
                    </Box>

                    {/* Description */}
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {facility.description}
                    </Typography>

                    {/* Capacity */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CapacityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {t('facilities:capacity', { count: facility.capacity })}
                      </Typography>
                    </Box>

                    {/* Booking Information */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      {t('facilities:sections.bookingInfo')}
                    </Typography>
                    <List dense>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TimeIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('facilities:booking.slotDurationLabel', { duration: bookingConfig.slotDuration })}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ScheduleIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('facilities:booking.advanceBookingLabel', { time: bookingConfig.leadTime })}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TimeIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('facilities:booking.maxDurationLabel', { duration: bookingConfig.maxDuration })}
                        />
                      </ListItem>
                    </List>

                    {/* Access Limits (if available) */}
                    {facility.accessInfo?.limits && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                          {t('facilities:sections.usageLimits')}
                        </Typography>
                        <List dense>
                          {facility.accessInfo.limits.usesPerDay && (
                            <ListItem sx={{ px: 0 }}>
                              <ListItemText 
                                primary={t('facilities:limits.daily', { count: facility.accessInfo.limits.usesPerDay })}
                              />
                            </ListItem>
                          )}
                          {facility.accessInfo.limits.usesPerWeek && (
                            <ListItem sx={{ px: 0 }}>
                              <ListItemText 
                                primary={t('facilities:limits.weekly', { count: facility.accessInfo.limits.usesPerWeek })}
                              />
                            </ListItem>
                          )}
                          {facility.accessInfo.limits.usesPerMonth && (
                            <ListItem sx={{ px: 0 }}>
                              <ListItemText 
                                primary={t('facilities:limits.monthly', { count: facility.accessInfo.limits.usesPerMonth })}
                              />
                            </ListItem>
                          )}
                        </List>
                      </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Operating Hours */}
                    <Typography variant="h6" gutterBottom>
                      {t('facilities:sections.operatingHours')}
                    </Typography>
                    <List dense>
                      {operatingHours.map((hours, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {hours.day}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color={hours.time === t('facilities:operatingHours.closed') ? 'error' : 'text.secondary'}
                                >
                                  {hours.time}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  {/* Book Now Button */}
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/facilities/${facility._id}/book`)}
                      disabled={facility.status !== 'active'}
                      sx={{
                        py: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      {facility.status === 'active' ? t('facilities:actions.bookNow') : t('facilities:actions.notAvailable')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default ViewFacilitiesPage;